import type { ComponentProps } from 'react';
import { Display, mq, StyleSheet } from 'react-native-unistyles';
import Button from '@/components/UI/Button';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';

interface ActionButtonBaseProps
  extends Omit<
    ComponentProps<typeof Button>,
    'appearance' | 'children' | 'shape'
  > {
  forceLabel?: boolean;
  iconVariant?: ComponentProps<typeof Icon>['variant'];
  labelVariant?: ComponentProps<typeof Text>['variant'];
}

interface ActionButtonIconProps extends ActionButtonBaseProps {
  icon: ComponentProps<typeof Icon>['name'];
  label?: string;
}

interface ActionButtonLabelProps extends ActionButtonBaseProps {
  icon?: ComponentProps<typeof Icon>['name'];
  label: string;
}

type ActionButtonProps = ActionButtonIconProps | ActionButtonLabelProps;

export default function ActionButton({
  icon,
  label,
  variant,
  labelVariant = 'typography',
  iconVariant = 'typography',
  ref,
  style: extraStyles,
  forceLabel = false,
  ...props
}: ActionButtonProps) {
  return (
    <Button
      ref={ref}
      appearance="outline"
      shape="circle"
      variant={variant}
      {...props}
      style={[style.container(forceLabel), extraStyles]}
    >
      {icon ? <Icon variant={iconVariant} size="xl" name={icon} /> : null}
      {forceLabel ? (
        <Text style={style.label} variant={labelVariant} size="md">
          {label}
        </Text>
      ) : label ? (
        <Display mq={mq.only.width('lg')}>
          <Text style={style.label} variant={labelVariant} size="md">
            {label}
          </Text>
        </Display>
      ) : null}
    </Button>
  );
}

const style = StyleSheet.create((theme) => ({
  container: (forceLabel: boolean) => ({
    gap: theme.gap(1.5),
    minWidth: theme.size(9),
    minHeight: theme.size(9),
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    paddingHorizontal: {
      base: forceLabel ? theme.gap(3) : theme.gap(1),
      lg: theme.gap(3),
    },
    paddingVertical: {
      base: theme.gap(0),
    },
  }),
  label: {
    fontWeight: theme.fontWeight.semibold,
  },
}));
